import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { newsService } from '../../services/newsService';
import { CircularProgress } from '@mui/material';
import { format, parseISO } from 'date-fns';
import Navbar from '../../components/navBar';

const NewsDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [news, setNews] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const formatDate = (dateString) => {
        try {
            const date = parseISO(dateString);
            if (isNaN(date.getTime())) {
                return 'Date not available';
            }
            return format(date, 'MMMM dd, yyyy');
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Date not available';
        }
    };

    useEffect(() => {
        const fetchNewsDetail = async () => {
            try {
                const data = await newsService.getNewsById(id);
                setNews(data);
                setLoading(false);
            } catch (err) {
                setError('Failed to fetch news article. Please try again later.');
                setLoading(false);
            }
        };

        fetchNewsDetail();
    }, [id]);

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <CircularProgress />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <p className="text-red-500">{error}</p>
            </div>
        );
    }

    if (!news) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <p className="text-red-500">News article not found</p>
            </div>
        );
    }

    return (
        <div className="bg-zinc-900 min-h-screen">
            <Navbar />
            <div className="pt-20 md:pt-24 pb-8">
                <div className="container mx-auto px-4">
                    <button 
                        onClick={() => navigate('/news')}
                        className="text-white hover:bg-zinc-800 p-2 rounded-full mb-6 transition-colors"
                    >
                        <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className="h-6 w-6" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                        >
                            <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M10 19l-7-7m0 0l7-7m-7 7h18" 
                            />
                        </svg>
                    </button>

                    <div className="bg-zinc-800 rounded-lg p-8 mb-8">
                        <h1 className="text-4xl font-bold text-white mb-4">
                            {news.headline}
                        </h1>

                        <p className="text-gray-400 mb-6">
                            {formatDate(news.published_at)}
                        </p>

                        {news.tags && news.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-8">
                                {news.tags.map((tag, index) => (
                                    <span 
                                        key={index}
                                        className="px-3 py-1 bg-zinc-700 text-gray-300 rounded-full text-sm hover:bg-zinc-600 transition-colors"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        )}

                        <div 
                            className="prose prose-invert max-w-none [&_img]:mx-auto [&_img]:h-[500px] [&_img]:w-[800px] [&_img]:object-contain [&_img]:rounded-lg [&_video]:mx-auto [&_video]:h-[500px] [&_video]:w-[800px] [&_video]:object-contain [&_video]:rounded-lg [&_strong]:font-bold [&_em]:italic [&_p]:text-gray-300 [&_h1]:text-3xl [&_h2]:text-2xl [&_h3]:text-xl [&_h4]:text-lg [&_h5]:text-base [&_h6]:text-sm"
                            dangerouslySetInnerHTML={{ __html: news.content }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewsDetail; 